import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../lib/config';
import { AuthCard, primaryButtonClass, ErrorBanner, NoticeBanner } from '../components/AuthCard';
import { useAuthCapabilities } from '../hooks/useAuthCapabilities';

export default function VerifyEmailNotice() {
  const [params] = useSearchParams();
  const caps = useAuthCapabilities();
  const email = params.get('email') ?? '';
  const next = params.get('next') || '/onboarding/create-org';
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const callbackURL = useMemo(() => `${window.location.origin}${next}`, [next]);

  async function resend() {
    if (!email) return;
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/send-verification-email`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email, callbackURL }),
      });
      const body = (await res.json().catch(() => null)) as { message?: string } | null;
      if (!res.ok) {
        setError(body?.message || 'Could not send verification email.');
        return;
      }
      setSuccess('Verification email sent.');
    } catch (err) {
      setError((err as Error).message || 'Could not send verification email.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthCard
      title="Check your email"
      subtitle="Verify your email so Oracord can continue your setup flow."
      footer={<Link to="/login" className="font-semibold text-amber-700 hover:text-amber-800 hover:underline">Back to sign in</Link>}
    >
      <ErrorBanner message={error} />
      {success && <NoticeBanner tone="emerald" className="mb-4">{success}</NoticeBanner>}
      {caps && !caps.emailVerificationRequired ? (
        <NoticeBanner tone="slate">
          Email verification is not required in this environment. You can continue to sign in normally.
        </NoticeBanner>
      ) : (
        <div className="space-y-4 text-sm text-slate-600">
          <p>
            {email ? <>We sent a verification link to <strong>{email}</strong>.</> : 'We sent a verification link to your email address.'}
          </p>
          <p>After you verify, Oracord will continue to your setup flow automatically.</p>
          <button type="button" onClick={resend} disabled={submitting || !email} className={primaryButtonClass}>
            {submitting ? 'Sending…' : 'Resend verification email'}
          </button>
        </div>
      )}
    </AuthCard>
  );
}
